import { Container, Sprite } from 'pixi.js';
import { PixiOverlayHost } from '../PixiOverlayHost';
import { NAMEPLATE } from '../../../shared/config/nameplate.config';
import { getStatusPipAtlas, type AtlasFrame } from './statusPipAtlas';
import { overflowLabel, type IPipRow, type PipOverflow, type PipRowStyle, type PipSlot } from './PipRow';

/** Digit positions in the overflow marker (`+99` at most). */
const DIGIT_SLOTS = 2;

/**
 * PixiPipRow — IPipRow as one Container of pooled sprites over one baked
 * atlas (statusPipAtlas.ts): `capacity` pip circles, a `+` and, per digit
 * position, one sprite for each digit 0–9. Every sprite keeps the frame it was
 * built with; showing `+3` instead of `+2` fades one digit sprite out and
 * another in. After the build, no update writes `visible`, `zIndex`,
 * `texture` or adds a child, so the row never rebuilds the stage (plan 13
 * §12, plan 15 §2.4). Calls before `ensureReady()` resolves are kept and
 * applied on build (PixiPlateBar precedent).
 */
export class PixiPipRow implements IPipRow {
  private root: Container | null = null;
  private pips: Sprite[] = [];
  private plus: Sprite | null = null;
  /** digits[position][digit] */
  private digits: Sprite[][] = [];
  private glyphWidth = new Map<string, number>();
  private destroyed = false;

  private lastPips: readonly PipSlot[] = [];
  private lastOverflow: PipOverflow | null = null;

  constructor(style: PipRowStyle) {
    void PixiOverlayHost.ensureReady().then(() => {
      if (this.destroyed) return;
      const atlas = getStatusPipAtlas(NAMEPLATE.name.fontFamily, style.overflowFontSize);
      const root = new Container();
      root.zIndex = style.depth;
      const pipScale = style.pipSize / atlas.circle.width;
      for (let i = 0; i < style.capacity; i++) {
        this.pips.push(this.slot(root, atlas.circle, pipScale));
      }
      // The atlas bakes glyphs at overflowFontSize: frames are already world-sized.
      for (const [g, frame] of Object.entries(atlas.glyphs)) this.glyphWidth.set(g, frame.width);
      this.plus = this.slot(root, atlas.glyphs['+'], 1, style.overflowColor);
      for (let p = 0; p < DIGIT_SLOTS; p++) {
        const row: Sprite[] = [];
        for (let d = 0; d <= 9; d++) row.push(this.slot(root, atlas.glyphs[String(d)], 1, style.overflowColor));
        this.digits.push(row);
      }
      this.root = root;
      PixiOverlayHost.addWorldChild(root);
      this.apply();
    });
  }

  update(pips: readonly PipSlot[], overflow: PipOverflow | null): void {
    this.lastPips = pips;
    this.lastOverflow = overflow;
    this.apply();
  }

  hide(): void {
    this.lastPips = [];
    this.lastOverflow = null;
    this.apply();
  }

  destroy(): void {
    this.destroyed = true;
    this.root?.destroy({ children: true });
    this.root = this.plus = null;
    this.pips = [];
    this.digits = [];
  }

  /** A sprite at alpha 0, anchored at its centre, `k` × the frame's world
   *  size, owning `frame` for life. */
  private slot(root: Container, frame: AtlasFrame, k: number, tint = 0xffffff): Sprite {
    const s = new Sprite(frame.texture);
    s.anchor.set(0.5, 0.5);
    s.scale.set((frame.width * k) / (frame.texture?.width || 1), (frame.height * k) / (frame.texture?.height || 1));
    s.tint = tint;
    s.alpha = 0;
    root.addChild(s);
    return s;
  }

  private apply(): void {
    if (!this.root) return;
    const pips = this.lastPips;
    for (let i = 0; i < this.pips.length; i++) {
      const s = this.pips[i];
      const pip = pips[i];
      if (!pip) { s.alpha = 0; continue; }
      s.position.set(pip.x, pip.y);
      s.tint = pip.color;
      s.alpha = pip.alpha;
    }
    this.applyOverflow(this.lastOverflow);
  }

  private applyOverflow(overflow: PipOverflow | null): void {
    const plus = this.plus;
    if (!plus) return;
    const label = overflow && overflow.count > 0 ? overflowLabel(overflow.count) : '';
    if (!overflow || !label) {
      plus.alpha = 0;
      for (const row of this.digits) for (const s of row) s.alpha = 0;
      return;
    }
    const total = [...label].reduce((w, ch) => w + (this.glyphWidth.get(ch) ?? 0), 0);
    let x = overflow.x - total / 2;
    const plusW = this.glyphWidth.get('+') ?? 0;
    plus.position.set(x + plusW / 2, overflow.y);
    plus.alpha = 1;
    x += plusW;
    for (let p = 0; p < this.digits.length; p++) {
      const ch = label[p + 1];
      const digit = ch === undefined ? -1 : Number(ch);
      const w = ch === undefined ? 0 : this.glyphWidth.get(ch) ?? 0;
      for (let d = 0; d < this.digits[p].length; d++) {
        const s = this.digits[p][d];
        if (d !== digit) { s.alpha = 0; continue; }
        s.position.set(x + w / 2, overflow.y);
        s.alpha = 1;
      }
      x += w;
    }
  }
}
